import React, { useState, useEffect } from 'react';
import { playerUpdates } from '../../api';
import UpdateCard from './UpdateCard';
import CoachReplyForm from './CoachReplyForm';
import './CoachUpdatesTab.css';

/**
 * CoachPlayerUpdatesTab - Coach views and replies to all updates from specific player
 */
const CoachPlayerUpdatesTab = ({ playerId, playerName, onBack }) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [replyingTo, setReplyingTo] = useState(null);

  useEffect(() => {
    if (playerId) {
      loadUpdates();
    }
  }, [playerId]);

  const loadUpdates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await playerUpdates.getPlayerUpdates(playerId);
      setUpdates(response.data.updates || []);
    } catch (err) {
      console.error('Error loading updates:', err);
      setError('Failed to load updates');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (updateId, replyText) => {
    try {
      await playerUpdates.replyToUpdate(updateId, playerId, replyText);
      setReplyingTo(null);
      await loadUpdates();
    } catch (err) {
      console.error('Error replying:', err);
      alert('Failed to send reply');
    }
  };

  const handleStatusChange = async (updateId, newStatus) => {
    try {
      await playerUpdates.updateStatus(updateId, playerId, newStatus);
      await loadUpdates();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handleDelete = async (updateId) => {
    if (!window.confirm('Delete this update? This action cannot be undone.')) {
      return;
    }

    try {
      await playerUpdates.deleteUpdate(updateId);
      setUpdates(updates.filter(u => u._id !== updateId));
    } catch (err) {
      console.error('Error deleting update:', err);
      alert('Failed to delete update');
    }
  };

  // Filter updates
  const filteredUpdates = filterType === 'all'
    ? updates
    : updates.filter(u => u.type === filterType);

  // Count pending updates
  const pendingCount = updates.filter(u => u.status === 'pending').length;

  return (
    <div className="coach-player-updates-tab">
      <div className="tab-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Players
        </button>
        <div>
          <h3>{playerName}'s Updates</h3>
          {pendingCount > 0 && (
            <span className="pending-badge">{pendingCount} pending</span>
          )}
        </div>
      </div>

      {error && (
        <div className="updates-error">
          ❌ {error}
        </div>
      )}

      {/* Filter Buttons */}
      {!loading && updates.length > 0 && (
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            All ({updates.length})
          </button>
          <button
            className={`filter-btn ${filterType === 'match' ? 'active' : ''}`}
            onClick={() => setFilterType('match')}
          >
            Matches ({updates.filter(u => u.type === 'match').length})
          </button>
          <button
            className={`filter-btn ${filterType === 'thought' ? 'active' : ''}`}
            onClick={() => setFilterType('thought')}
          >
            Thoughts ({updates.filter(u => u.type === 'thought').length})
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="updates-loading">
          Loading updates...
        </div>
      )}

      {/* Empty State */}
      {!loading && updates.length === 0 && (
        <div className="empty-state">
          <p>No updates from this player yet</p>
          <p>Check back when they share matches or thoughts</p>
        </div>
      )}

      {/* Updates Feed */}
      {!loading && filteredUpdates.length > 0 && (
        <div className="updates-list">
          {filteredUpdates.map(update => (
            <div key={update._id} className="update-item">
              <UpdateCard
                update={update}
                onDelete={() => handleDelete(update._id)}
                isOwnUpdate={false}
              />

              {/* Coach Actions */}
              <div className="coach-actions">
                <div className="action-row">
                  <select
                    className="status-select"
                    value={update.status}
                    onChange={(e) => handleStatusChange(update._id, e.target.value)}
                  >
                    <option value="pending">⏳ Pending</option>
                    <option value="answered">✅ Answered</option>
                    <option value="resolved">✔️ Resolved</option>
                  </select>

                  {!update.coachReply && (
                    <button
                      className="reply-btn"
                      onClick={() => setReplyingTo(update._id)}
                    >
                      💬 Reply
                    </button>
                  )}
                </div>

                {/* Reply Form */}
                {replyingTo === update._id && (
                  <CoachReplyForm
                    onSubmit={(replyText) => handleReply(update._id, replyText)}
                    onCancel={() => setReplyingTo(null)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No filtered results */}
      {!loading && updates.length > 0 && filteredUpdates.length === 0 && (
        <div className="empty-state">
          <p>No {filterType}s found</p>
        </div>
      )}
    </div>
  );
};

export default CoachPlayerUpdatesTab;
